import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Map, Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { Mono } from '@/components/common/display';
import { NotImplementedState, LoadingRows } from '@/components/common/Feedback';
import { SitesMap } from '@/components/resource/SitesMap';
import type { SitePin } from '@/components/resource/SitesMap';
import { CreateSiteDialog } from '@/components/resource/CreateSiteDialog';
import { useStubResource, isNotImplemented } from '@/hooks/use-resource';

interface SiteItem {
  id: string;
  code: string;
  name: string;
  lat: number;
  lng: number;
  description: string;
}

const COLUMNS: ColumnDef<SiteItem>[] = [
  {
    accessorKey: 'code',
    header: 'Código',
    cell: ({ row }) => <Mono className="text-xs font-semibold">{row.original.code}</Mono>,
  },
  {
    accessorKey: 'name',
    header: 'Nombre',
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: 'lat',
    header: 'Latitud',
    cell: ({ row }) =>
      row.original.lat != null ? (
        <Mono className="text-xs">{row.original.lat.toFixed(4)}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'lng',
    header: 'Longitud',
    cell: ({ row }) =>
      row.original.lng != null ? (
        <Mono className="text-xs">{row.original.lng.toFixed(4)}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'description',
    header: 'Descripción',
    cell: ({ row }) =>
      row.original.description ? (
        <span className="text-xs text-muted-foreground">{row.original.description}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

export const Route = createFileRoute('/_auth/sites')({
  component: SitesPage,
});

function SitesPage() {
  const { data, isLoading, error } = useStubResource<SiteItem>('sites');
  const [createOpen, setCreateOpen] = useState(false);

  const sites = data?.data ?? [];

  const sitePins: SitePin[] = sites
    .filter((s) => s.lat != null && s.lng != null)
    .map((s) => ({ id: s.id, code: s.code, name: s.name, lat: s.lat, lng: s.lng }));

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Sitios"
        icon={Map}
        description="Ubicaciones físicas y geográficas de la red"
        actions={
          <Button size="sm" className="h-7 text-xs" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 size-3.5" />
            Nuevo sitio
          </Button>
        }
      />
      <CreateSiteDialog open={createOpen} onOpenChange={setCreateOpen} />

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
        {/* Map always rendered — shows empty world when no data */}
        <SitesMap sites={sitePins} />

        {isLoading ? (
          <div className="space-y-2 p-2">
            <LoadingRows rows={6} />
          </div>
        ) : isNotImplemented(error) ? (
          <NotImplementedState resource="Sitios" />
        ) : (
          <DataTable
            columns={COLUMNS}
            data={sites}
            getRowId={(r) => r.id}
            error={!isNotImplemented(error) ? error : undefined}
            emptyTitle="Sin sitios"
            emptyDescription="No hay sitios registrados."
            csvFilename="sites"
          />
        )}
      </div>
    </div>
  );
}
